import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import Box from "@mui/material/Box";
import FormControl from "@mui/material/FormControl";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import { styled } from "@mui/material/styles";
import { useState } from "react";
import { EXAMPLE_PROBLEMS } from "../constants";
// #region: Styled Components
const Card = styled(Box)(({ theme }) => ({
    padding: 16,
    borderRadius: 10,
    background: theme.palette.background.paper,
    boxShadow: "0 4px 10px rgba(12, 20, 30, 0.06)",
    border: `1px solid ${theme.palette.divider}`,
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    transition: "transform 0.12s, box-shadow 0.12s",
    cursor: "pointer",
    ":hover": {
        boxShadow: "0 10px 30px rgba(12,20,30,0.12)",
        backgroundImage: `linear-gradient(90deg, ${theme.palette.primary.main}18, ${theme.palette.secondary.main}12)`,
    },
    ":focus": {
        outline: `2px solid ${theme.palette.action.focus}`,
        outlineOffset: 2,
    },
    ":hover .start-badge": {
        opacity: 1,
        transform: "translateX(0)",
    },
    ":focus .start-badge": {
        opacity: 1,
        transform: "translateX(0)",
    },
}));
const Desc = styled(Box)(({ theme }) => `
    color: ${theme.palette.text.secondary};
    font-size: 0.9rem;
    margin-bottom: 12px;
`);
const Grid = styled(Box) `
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 12px;
`;
const HeaderRow = styled(Box)(() => `
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    margin-bottom: 12px;
`);
const Heading = styled("h2")(({ theme }) => `
    margin: 0;
    font-size: 1.25rem;
    color: ${theme.palette.text.primary};
`);
const ObjectivesRow = styled(Box)(({ theme }) => `
    color: ${theme.palette.text.primary};
    font-size: 0.9rem;
    margin-bottom: 12px;
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
    align-items: start;
    div {
        display: block;
        word-break: break-word;
    }
`);
const ScrollArea = styled(Box)(({ theme }) => ({
    overflowY: "auto",
    paddingTop: theme.spacing(1),
    paddingRight: theme.spacing(1),
    paddingBottom: theme.spacing(1),
}));
const StartBadge = styled(Box)(({ theme }) => ({
    marginLeft: "auto",
    fontSize: 12,
    fontWeight: 700,
    color: theme.palette.primary.main,
    padding: `${theme.spacing(0.5)} ${theme.spacing(1)}`,
    background: theme.palette.background.default,
    opacity: 0,
    transform: "translateX(6px)",
    transition: "opacity 140ms ease, transform 140ms ease",
}));
const Subtitle = styled(Box)(({ theme }) => `
    color: ${theme.palette.text.secondary};
    font-size: 0.9rem;
    margin-top: 4px;
`);
const Title = styled("h3")(({ theme }) => `
    margin: 0;
    font-size: 1rem;
    color: ${theme.palette.text.primary};
`);
const TitleRow = styled(Box) `
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 8px;
`;
// #endregion: Types
const ExamplesSelection = ({ onPrefill, onStartSession }) => {
    const [selectedDomain, setSelectedDomain] = useState("");
    const onChoose = (q, o1, o2) => {
        const payload = {
            question: q,
            objective1: o1,
            objective2: o2,
            context_value: "",
        };
        onPrefill(payload);
        if (typeof onStartSession === "function") {
            onStartSession(payload);
        }
    };
    const domains = Object.keys(EXAMPLE_PROBLEMS);
    const examplesForDomain = (domain) => (domain ? Object.keys(EXAMPLE_PROBLEMS[domain]) : []);
    const filteredItems = () => {
        if (selectedDomain) {
            return (examplesForDomain(selectedDomain) || []).map((ex) => ({
                key: `${selectedDomain}-${ex}`,
                domain: selectedDomain,
                ex,
                data: EXAMPLE_PROBLEMS[selectedDomain][ex],
            }));
        }
        return Object.keys(EXAMPLE_PROBLEMS).flatMap((domain) => Object.keys(EXAMPLE_PROBLEMS[domain]).map((ex) => ({
            key: `${domain}-${ex}`,
            domain,
            ex,
            data: EXAMPLE_PROBLEMS[domain][ex],
        })));
    };
    return (_jsxs(Box, { children: [_jsxs(HeaderRow, { children: [_jsxs(Box, { children: [_jsx(Heading, { children: "Start from an example" }), _jsxs(Subtitle, { children: [_jsx("strong", { children: "Pick a starting point." }), " Click an example below, or click \u2630 in the top left for a custom topic."] })] }), _jsx(FormControl, { variant: "standard", size: "small", sx: { minWidth: 120 }, children: _jsxs(Select, { value: selectedDomain, displayEmpty: true, renderValue: (val) => val || "Domain", onChange: (e) => setSelectedDomain(e.target.value), children: [_jsx(MenuItem, { value: "", children: "All domains" }), domains.map((d) => (_jsx(MenuItem, { value: d, children: d }, d)))] }) })] }), _jsx(ScrollArea, { children: _jsx(Grid, { children: filteredItems().map((item) => (_jsxs(Card, { "data-testid": `examples-card_${item.key.replace(/\s+/gu, "_")}-div`, role: "button", tabIndex: 0, onClick: () => onChoose(item.data.question, item.data.objective1, item.data.objective2), onKeyDown: (e) => {
                            if (e.key === "Enter" || e.key === " ") {
                                onChoose(item.data.question, item.data.objective1, item.data.objective2);
                            }
                        }, children: [_jsxs(TitleRow, { children: [_jsx(Title, { children: item.ex }), _jsx(StartBadge, { className: "start-badge", children: "START SESSION" })] }), _jsx(Desc, { children: item.data.question }), _jsxs(ObjectivesRow, { children: [_jsxs(Box, { children: [_jsx("strong", { children: "Objective 1: " }), " ", item.data.objective1] }), _jsxs(Box, { children: [_jsx("strong", { children: "Objective 2: " }), " ", item.data.objective2] })] })] }, item.key))) }) })] }));
};
export default ExamplesSelection;
